import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface DashboardFilters {
  // Test orders filter
  includeTestOrder: boolean;
  
  // Marketplace filters
  marketplaceFilter: string[];
  
  // Time period filter
  timePeriod: 'day' | 'week' | 'month' | 'year';
  
  // Search filter
  searchQuery: string;
  
  // Date range filters
  dateFrom: string | null;
  dateTo: string | null;
}

export interface FulfillmentFilters {
  // Order status filter
  status: string;
  
  // Sub-status filter (for payment verification and awaiting resolution)
  subStatus: string;
  
  // Marketplace filters
  marketplaceFilter: string[];
  
  // Search filter
  searchQuery: string;
  
  // Date range filters
  dateFrom: string | null;
  dateTo: string | null;
  
  // Pagination
  page: number;
  limit: number;
}

interface FilterState {
  // Current filters
  filters: DashboardFilters;
  
  // Fulfillment-specific filters
  fulfillmentFilters: FulfillmentFilters;
  
  // Available marketplace options
  availableMarketplaces: Array<{
    code: string;
    name: string;
    flag?: string;
  }>;
  
  // Loading state
  isLoading: boolean;
}

interface FilterActions {
  // Update individual filters
  setIncludeTestOrder: (include: boolean) => void;
  setMarketplaceFilter: (marketplaces: string[]) => void;
  setTimePeriod: (period: DashboardFilters['timePeriod']) => void;
  setSearchQuery: (query: string) => void;
  setDateRange: (from: string | null, to: string | null) => void;
  
  // Toggle marketplace filter
  toggleMarketplace: (marketplaceCode: string) => void;
  
  // Clear specific filters
  clearMarketplaceFilter: () => void;
  clearSearchQuery: () => void;
  clearDateRange: () => void;
  
  // Clear all filters
  clearAllFilters: () => void;
  
  // Fulfillment-specific actions
  setFulfillmentStatus: (status: string) => void;
  setFulfillmentSubStatus: (subStatus: string) => void;
  setFulfillmentMarketplaceFilter: (marketplaces: string[]) => void;
  setFulfillmentSearchQuery: (query: string) => void;
  setFulfillmentDateRange: (from: string | null, to: string | null) => void;
  setFulfillmentPagination: (page: number, limit: number) => void;
  toggleFulfillmentMarketplace: (marketplaceCode: string) => void;
  clearFulfillmentFilters: () => void;
  getFulfillmentApiFilters: () => Record<string, any>;
  
  // Set available marketplaces
  setAvailableMarketplaces: (marketplaces: Array<{ code: string; name: string; flag?: string }>) => void;
  
  // Set loading state
  setLoading: (loading: boolean) => void;
  
  // Get current filters for API
  getApiFilters: () => Record<string, any>;
}

type FilterStore = FilterState & FilterActions;

const defaultFilters: DashboardFilters = {
  includeTestOrder: false,
  marketplaceFilter: [],
  timePeriod: 'week',
  searchQuery: '',
  dateFrom: null,
  dateTo: null
};

const defaultFulfillmentFilters: FulfillmentFilters = {
  status: 'outstanding',
  subStatus: '', // Clear subStatus by default so status takes precedence
  marketplaceFilter: [], // Empty array to show all marketplaces when cleared
  searchQuery: '',
  dateFrom: null,
  dateTo: null,
  page: 1,
  limit: 10
};

export const useFilterStore = create<FilterStore>()(
  persist(
    (set, get) => ({
      // Initial state
      filters: defaultFilters,
      fulfillmentFilters: defaultFulfillmentFilters,
      availableMarketplaces: [],
      isLoading: false,

      // Actions
      setIncludeTestOrder: (include) => 
        set((state) => ({
          filters: { ...state.filters, includeTestOrder: include }
        })),
      
      setMarketplaceFilter: (marketplaces) => 
        set((state) => ({
          filters: { ...state.filters, marketplaceFilter: marketplaces }
        })),
      
      setTimePeriod: (period) => 
        set((state) => ({
          filters: { ...state.filters, timePeriod: period }
        })),
      
      setSearchQuery: (query) => 
        set((state) => ({
          filters: { ...state.filters, searchQuery: query }
        })),
      
      setDateRange: (from, to) => 
        set((state) => ({
          filters: { ...state.filters, dateFrom: from, dateTo: to }
        })),
      
      toggleMarketplace: (marketplaceCode) => 
        set((state) => {
          const currentFilters = state.filters.marketplaceFilter;
          // Convert country code to marketplace format (e.g., 'UK' -> 'uk_mkt')
          const marketplaceFormat = `${marketplaceCode.toLowerCase()}_mkt`;
          const newFilters = currentFilters.includes(marketplaceFormat)
            ? currentFilters.filter(code => code !== marketplaceFormat)
            : [...currentFilters, marketplaceFormat];
          
          return {
            filters: { ...state.filters, marketplaceFilter: newFilters }
          };
        }),
      
      clearMarketplaceFilter: () => 
        set((state) => ({
          filters: { ...state.filters, marketplaceFilter: [] }
        })),
      
      clearSearchQuery: () => 
        set((state) => ({
          filters: { ...state.filters, searchQuery: '' }
        })),
      
      clearDateRange: () => 
        set((state) => ({
          filters: { ...state.filters, dateFrom: null, dateTo: null }
        })),
      
      clearAllFilters: () => 
        set({ filters: defaultFilters }),
      
      // Fulfillment-specific actions
      setFulfillmentStatus: (status) => 
        set((state) => ({
          fulfillmentFilters: { ...state.fulfillmentFilters, status, subStatus: '' }
        })),
      
      setFulfillmentSubStatus: (subStatus) => 
        set((state) => ({
          fulfillmentFilters: { ...state.fulfillmentFilters, subStatus }
        })),
      
      setFulfillmentMarketplaceFilter: (marketplaces) => 
        set((state) => ({
          fulfillmentFilters: { ...state.fulfillmentFilters, marketplaceFilter: marketplaces }
        })),
      
      setFulfillmentSearchQuery: (query) => 
        set((state) => ({
          fulfillmentFilters: { ...state.fulfillmentFilters, searchQuery: query }
        })),
      
      setFulfillmentDateRange: (from, to) => 
        set((state) => ({
          fulfillmentFilters: { ...state.fulfillmentFilters, dateFrom: from, dateTo: to }
        })),
      
      setFulfillmentPagination: (page, limit) => 
        set((state) => ({
          fulfillmentFilters: { ...state.fulfillmentFilters, page, limit }
        })),
      
      toggleFulfillmentMarketplace: (marketplaceCode) => 
        set((state) => {
          const currentFilters = state.fulfillmentFilters.marketplaceFilter;
          // Convert country code to marketplace format (e.g., 'UK' -> 'uk_mkt')
          const marketplaceFormat = `${marketplaceCode.toLowerCase()}_mkt`;
          const newFilters = currentFilters.includes(marketplaceFormat)
            ? currentFilters.filter(code => code !== marketplaceFormat)
            : [...currentFilters, marketplaceFormat];
          
          return {
            fulfillmentFilters: { ...state.fulfillmentFilters, marketplaceFilter: newFilters }
          };
        }),
      
      clearFulfillmentFilters: () => 
        set({ fulfillmentFilters: defaultFulfillmentFilters }),
      
      getFulfillmentApiFilters: () => {
        const { fulfillmentFilters } = get();
        // Use subStatus if available, otherwise fall back to main status
        const statusToUse = fulfillmentFilters.subStatus || fulfillmentFilters.status;
        const filters = {
          type: statusToUse,
          search: fulfillmentFilters.searchQuery || undefined,
          marketplace_filter: fulfillmentFilters.marketplaceFilter.length > 0 ? fulfillmentFilters.marketplaceFilter : undefined,
          date_from: fulfillmentFilters.dateFrom || undefined,
          date_to: fulfillmentFilters.dateTo || undefined,
          page: fulfillmentFilters.page,
          limit: fulfillmentFilters.limit
        };
        console.log('🔍 getFulfillmentApiFilters - Current state:', { status: fulfillmentFilters.status, subStatus: fulfillmentFilters.subStatus }); // Debug log
        console.log('🔍 getFulfillmentApiFilters - Generated filters:', filters); // Debug log
        return filters;
      },
      
      setAvailableMarketplaces: (marketplaces) => 
        set({ availableMarketplaces: marketplaces }),
      
      setLoading: (loading) => set({ isLoading: loading }),
      
      getApiFilters: () => {
        const { filters } = get();
        return {
          include_test_order: filters.includeTestOrder,
          marketplace_filter: filters.marketplaceFilter,
          time_period: filters.timePeriod,
          search_query: filters.searchQuery,
          date_from: filters.dateFrom,
          date_to: filters.dateTo
        };
      }
    }),
    {
      name: 'filter-storage',
      partialize: (state) => ({
        filters: state.filters,
        fulfillmentFilters: state.fulfillmentFilters
      })
    }
  )
);
